/**
 * useEpgCard.ts
 */
import Card from '@shared/types/props/card';
import DateUtils from '@shared/utils/date';
import EpgEvent from '@shared/classes/epgEvent';
import MainContext from '@shared/components/utils/context/mainContext';
import { selectMagicRemoteEnabled } from '@shared/store/slices/magicRemote.slice';
import { selectCurrentParams } from '@shared/store/slices/router.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useContext, useMemo, useRef, useState } from 'react';
import { MainTimeline } from '@shared/types/interfaces/main';

/**
 * Epg card constants
 */
const MIN_WIDTH = 6.25;

/**
 * Hook for the epg card component.
 */
export default function useEpgCard({ focused, item, onClick }: Card) {
  // Context
  const { config, hoverAsset } = useContext(MainContext);

  // Selectors
  const enabled = useAppSelector(selectMagicRemoteEnabled);
  const currentParams = useAppSelector(selectCurrentParams);

  // States
  const [hovered, setHovered] = useState(false);

  // References
  const ref = useRef<HTMLDivElement>(null);

  // Event handlers
  const onMouseEnter = () => {
    setHovered(true);
    hoverAsset(item);
  };

  const onMouseLeave = () => {
    setHovered(false);
  };

  // Memoized data
  const { props } = useMemo(() => {
    const timeline = currentParams?.timeline as MainTimeline | undefined;
    const indicator = document.getElementById('indicatorBody');
    const isCurrentDate = DateUtils.isCurrentDate(
      (item as EpgEvent)?.date?.start ?? 0,
      (item as EpgEvent)?.date?.end ?? 0
    );
    const isPastDate = DateUtils.isPastDate((item as EpgEvent)?.date?.end ?? 0);
    const isWithinTimeline =
      (timeline?.start?.getTime() ?? 0) <=
        ((item as EpgEvent)?.date?.start ?? 0) ||
      (timeline?.end?.getTime() ?? 0) >= ((item as EpgEvent)?.date?.end ?? 0);
    const isWrappingTimeline =
      (timeline?.start?.getTime() ?? 0) >=
        ((item as EpgEvent)?.date?.start ?? 0) &&
      (timeline?.end?.getTime() ?? 0) <= ((item as EpgEvent)?.date?.end ?? 0);
    const duration = DateUtils.getDurationInHours(
      Math.max(
        (item as EpgEvent).date?.start ?? 0,
        config?.timeline?.start?.getTime() ?? 0
      ),
      (item as EpgEvent).date?.end
    );
    const progress =
      isCurrentDate && ref.current?.getBoundingClientRect()
        ? (indicator?.getBoundingClientRect().x ?? 0) -
          (ref.current?.getBoundingClientRect().x ?? 0)
        : 0;
    const width = 100 * (duration / (config.timeline?.offset ?? 0));
    const displayContent =
      width > MIN_WIDTH &&
      (timeline ? isWithinTimeline || isWrappingTimeline : true);
    const displayTitle = width > MIN_WIDTH;
    return {
      props: {
        container: {
          displayContent,
          hasFocus: focused || hovered,
          isPast: isPastDate,
          progress,
          width,
          onClick,
          onMouseEnter,
          onMouseLeave,
        },
        title: {
          displayContent: displayTitle,
        },
      },
    };
  }, [
    onClick,
    onMouseEnter,
    onMouseLeave,
    config?.timeline,
    enabled,
    hovered,
    currentParams?.timeline,
    item,
  ]);

  // Return values
  return { props, ref };
}
